import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTranslation } from '../hooks/useTranslation';

/**
 * /abonnement/bekreft — landing page after the Vipps redirect.
 *
 * There is no webhook: GET /api/subscription/status lazily reconciles the
 * PENDING agreement against Vipps, so we poll it until the subscription
 * turns active (or clearly won't). Polling goes through the context's
 * refresh() so the app shell's gate state stays in sync.
 */

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 40; // ≈ 2 minutes

export default function SubscriptionConfirm() {
  const navigate = useNavigate();
  const { refresh } = useSubscription();
  const { t } = useTranslation();
  // 'polling' | 'success' | 'failed' (stopped/expired) | 'timeout'
  const [phase, setPhase] = useState('polling');

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let polls = 0;

    const poll = async () => {
      polls += 1;
      const next = await refresh(); // resolves with current state, never rejects
      if (cancelled) return;

      if (next.status === 'active') {
        setPhase('success');
        return;
      }
      if (next.status === 'stopped' || next.status === 'expired') {
        setPhase('failed');
        return;
      }
      if (polls >= MAX_POLLS) {
        setPhase('timeout');
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Mount-only polling loop; refresh is stable enough and deduped in context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="-mx-4 -mb-4 -mt-[70px] md:-m-8 min-h-screen bg-background-card px-4 pt-[86px] pb-12 md:pt-14">
      <div className="max-w-md mx-auto">
        <div className="bg-background rounded-2xl border border-border-light shadow-sm p-8 text-center">
          {phase === 'polling' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <h1 className="text-xl font-semibold text-sage mb-2">{t('subscriptionConfirm.waitingTitle')}</h1>
              <p className="text-text-secondary">{t('subscriptionConfirm.waitingBody')}</p>
            </>
          )}

          {phase === 'success' && (
            <>
              <div className="w-12 h-12 rounded-full bg-success-100 text-success-700 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-sage mb-2">{t('subscriptionConfirm.successTitle')}</h1>
              <p className="text-text-secondary mb-6">{t('subscriptionConfirm.successBody')}</p>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="w-full min-h-[48px] rounded-xl bg-primary hover:bg-primary-600 text-white font-semibold px-6 py-3 transition-colors"
              >
                {t('subscriptionConfirm.successCta')}
              </button>
            </>
          )}

          {(phase === 'failed' || phase === 'timeout') && (
            <>
              <h1 className="text-xl font-semibold text-sage mb-2">
                {phase === 'failed'
                  ? t('subscriptionConfirm.failedTitle')
                  : t('subscriptionConfirm.timeoutTitle')}
              </h1>
              <p className="text-text-secondary mb-6">
                {phase === 'failed'
                  ? t('subscriptionConfirm.failedBody')
                  : t('subscriptionConfirm.timeoutBody')}
              </p>
              <button
                onClick={() => navigate('/abonnement')}
                className="w-full min-h-[48px] rounded-xl bg-primary hover:bg-primary-600 text-white font-semibold px-6 py-3 transition-colors"
              >
                {t('subscriptionConfirm.tryAgain')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
